import { useEffect } from "react";
import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import { $api } from "@/lib/api";

/**
 * Fire all dashboard prefetch queries against the given QueryClient.
 * Callable from outside React (e.g. after token exchange in useAuth).
 * TanStack Query deduplicates in-flight requests with the same key,
 * so calling this multiple times is safe.
 */
export function fireDashboardPrefetches(qc: QueryClient) {
  qc.prefetchQuery($api.queryOptions("get", "/api/v1/projects"));
  qc.prefetchQuery($api.queryOptions("get", "/api/v1/home/activity"));
  qc.prefetchQuery(
    $api.queryOptions("get", "/api/v1/meetings", {
      params: { query: { mode: "upcoming", days_ahead: 14 } },
    }),
  );
  qc.prefetchQuery(
    $api.queryOptions("get", "/api/v1/meetings", {
      params: { query: { mode: "upcoming", days_ahead: 30 } },
    }),
  );
  qc.prefetchQuery(
    $api.queryOptions("get", "/api/v1/meetings/enriched", {
      params: { query: { limit: 100 } },
    }),
  );
  qc.prefetchQuery($api.queryOptions("get", "/api/v1/precedents"));
}

/**
 * Prefetches dashboard data as soon as the backend token is available.
 * Fires all fetches in parallel so they populate the
 * TanStack Query cache before any tab component mounts.
 */
export function useDashboardPrefetch(backendToken: string | null | undefined) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!backendToken) return;
    fireDashboardPrefetches(queryClient);
  }, [backendToken, queryClient]);
}

/**
 * Prefetches project-specific intelligence data when navigating
 * to a project detail page.
 */
export function useProjectPrefetch(projectId: string | undefined) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!projectId) return;

    queryClient.prefetchQuery(
      $api.queryOptions("get", "/api/v1/project-memory/{project_id}", {
        params: { path: { project_id: projectId } },
      }),
    );

    queryClient.prefetchQuery(
      $api.queryOptions("get", "/api/v1/projects/{project_id}/members", {
        params: { path: { project_id: projectId } },
      }),
    );

    queryClient.prefetchQuery(
      $api.queryOptions("get", "/api/v1/meetings/by-project/{project_id}", {
        params: { path: { project_id: projectId } },
      }),
    );
  }, [projectId, queryClient]);
}
